// [목적] 모바일 /chat — 채팅 버블 목록 + 하단 생성 상태 시트 + 입력창
// [이유] Spec(docs/specs/Approved/2026-07-05_fe-rag-generate-rewire.md) §7 Resolved Decisions(2026-07-19):
//        ChatPage(데스크톱)와 동일하게 죽은 /plan API 배선을 useChatFlow(hooks/useChatFlow.ts) 로 교체한다.
//        네트워크·상태 로직은 훅에 위임하고, 이 파일은 모바일 전용 시각 언어(비대칭 radius·하단 시트)만 담당.
//        구 SlotFillingState 기반 로직(TripInfoCard·PreviewMiniCard·QuickChips 필터·SSE 진행률·?sid 복구)은
//        신 계약에 대응 필드가 없어 전부 제거됨(D-1, D-3~D-8 불요화).
// [사이드 이팩트] useChatFlow → rag.client(createConversation/turn/submitSlots) + useFolioGeneration 폴링.
// [수정 시 고려사항] IME 가드(e.nativeEvent.isComposing) 보존 필수 — 제거 시 "30만원" 분리 전송 버그 재발.
//                   모바일 비대칭 radius('16px 16px 4px 16px' 등)는 모바일 디자인 언어 — 토큰 미정의 고정값 허용(기존 관행).
//                   [G1-1, 2026-09-17] pdf-ready 버블에 msg.portfolioId 기반 "폴리오 보기"(/folio/:id) 주 버튼
//                   추가 — PDF 다운로드는 부가 버튼으로 유지, portfolioId null 이면 기존 PDF 버튼만 노출.
//                   [G1-3, 2026-09-17] ChatPage(데스크톱)와 동일한 useAuthGate('chat.create') + LoginPromptModal
//                   적용 완료 — 더 이상 "useAuthGate 미적용" 아님(D-9 후속). e2e/auth-gate.e2e.ts 의 모바일
//                   skip 제거 대상.
//                   [FlowLine 83963a6b, 2026-09-18] 첫 진입 추천 컨셉 칩(GET /rag/concepts, useConcepts) 신규
//                   추가 — ChatPage(데스크톱)와 동일 데이터 소스·클릭 흐름(sendConceptChip)을 모바일 입력창
//                   바로 위에 배치. 로딩 스켈레톤, 실패/빈 배열 시 영역 숨김도 데스크톱과 동일 패턴.

import { Box, ButtonBase, LinearProgress, Skeleton, TextField, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { buildPdfUrl } from '../../api/rag.client.js';
import type { Concept } from '../../api/concepts.client.js';
import { LoginPromptModal } from '../../components/auth/LoginPromptModal.js';
import { useAuthGate } from '../../hooks/useAuthGate.js';
import { useChatFlow, type ChatMsg } from '../../hooks/useChatFlow.js';
import { useConcepts } from '../../hooks/useConcepts.js';
import { useTypewriter } from '../../hooks/useTypewriter.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const BORDER = c.line.base[L];
const PAPER0 = c.paper[0][L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
const GREEN = c.accent[500][L];
const SHADOW_CARD = tokens.shadow.card;

// [목적] 생성 잡 단계(stage) → 한글 라벨 매핑 (ChatPage.tsx STAGE_LABELS 와 동일 — 동기 갱신 필요)
const STAGE_LABELS: Record<string, string> = {
  extract: '여행 정보 정리 중...',
  search: '장소 탐색 중...',
  generate: '이야기 생성 중...',
  render: 'PDF 만드는 중...',
};

export function ChatMobile(): JSX.Element {
  const flow = useChatFlow();
  // [목적] G1-3: ChatPage(데스크톱)와 동일한 게스트 가드 — send/clarify/confirm 진입 시 로그인 유도
  // [이유] D-9 는 데스크톱만 적용 범위였고(기존 주석 "ChatMobile 은 useAuthGate 미적용"), 모바일 채팅도
  //        동일하게 인증이 필요한 생성 액션이므로 게스트가 우회할 수 없어야 한다(패리티 확보).
  // [사이드 이팩트] LoginPromptModal 렌더 추가 — authGuard 로 래핑된 콜백은 인증 상태 확인 후에만 실행.
  // [수정 시 고려사항] intent 는 ChatPage.tsx 와 동일하게 'chat.create' 고정.
  const { guard: authGuard, isPromptOpen, closePrompt, pendingIntent } = useAuthGate('chat.create');
  const { data: concepts, isLoading: conceptsLoading, error: conceptsError } = useConcepts();
  // [목적] 칩 영역 노출 조건 — ChatPage.tsx showConceptChips 와 동일 패턴(로딩 중이거나 성공+1건 이상)
  const showConceptChips = conceptsLoading || (!conceptsError && (concepts?.length ?? 0) > 0);
  // [목적] 홈 모바일 히어로에서 넘어온 발화(state.input)를 입력창 시드로 복원 — 구 ChatMobile 동작 보존
  // [이유] HomeMobile 이 navigate('/chat', {state:{input}}) 로 입력값을 전달(진입점 /chat 복귀에 따른 원상복귀).
  // [사이드 임팩트] 없음 — 초기값만. 자동 전송 안 함(사용자가 전송 버튼 확인).
  // [수정 시 고려사항] state 는 새로고침 시 유실(React Router state 특성) — 시드 용도로만 사용.
  const location = useLocation();
  const seedText = (location.state as { input?: string } | null)?.input ?? '';
  const [input, setInput] = useState(seedText);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [flow.msgs, flow.sending]);

  const isBusy = flow.sending || flow.phase === 'generating';
  const stageLabel = flow.generation.stage ? STAGE_LABELS[flow.generation.stage] : undefined;

  const handleSend = useCallback((): void => {
    const t = input;
    authGuard(() => {
      setInput('');
      void flow.send(t);
    });
  }, [authGuard, flow, input]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
   * [목적] 추천 컨셉 칩 클릭 — samplePrompt 전송 + concept 선택값 기록(ChatPage.tsx handleConceptChip 미러)
   * [이유] authGuard 게이트를 동일하게 거쳐야 한다(G1-3 게스트 가드 패리티).
   * [사이드 이팩트] flow.sendConceptChip 이 send() 재사용 + selectedConcept 로컬 기록.
   * [수정 시 고려사항] 없음.
   */
  const handleConceptChip = useCallback(
    (concept: Concept): void => {
      authGuard(() => {
        setInput('');
        void flow.sendConceptChip(concept.samplePrompt, concept.id);
      });
    },
    [authGuard, flow],
  );

  /**
   * [목적] session-expired 버블 "새 대화 시작" 클릭 — 훅 상태 리셋 + 실패 직전 원문을 입력창에 복원(G1-2)
   * [이유] ChatPage.tsx(데스크톱)와 동일 AC: "새 대화 시작 + 마지막 입력 복원".
   * [사이드 이팩트] setInput(restoreText) — restoreText 가 빈 문자열이면 입력창은 그대로 비워짐.
   * [수정 시 고려사항] 없음.
   */
  const handleStartNewConversation = useCallback(
    (restoreText: string): void => {
      flow.startNewConversation();
      setInput(restoreText);
    },
    [flow],
  );

  return (
    // [목적] 채팅 화면 루트 — 헤더/메시지/입력바를 세로로 쌓는 고정 높이 컬럼
    // [이유] 2026-09-18 QA(H-1): `minHeight: '100vh'` 는 App.tsx <main> 이 하단 탭바를 위해 확보한
    //         pb(70px + safe-area)를 무시하고 뷰포트 전체 높이를 점유한다. 그 결과 컬럼 맨 아래의
    //         입력창이 fixed 탭바 뒤로 20px 밀려 들어가 모바일에서 입력이 가려졌다.
    //         main 은 이미 flex column + 하단 패딩을 가지므로 flex:1 로 그 content box 만 채운다.
    // [사이드 이팩트] 채팅 화면이 더 이상 문서 스크롤을 만들지 않는다 — 메시지 영역 내부 스크롤만 사용.
    // [수정 시 고려사항] 입력바를 화면 하단에 붙이는 레이아웃이므로 100vh/100dvh 로 되돌리지 말 것
    //                    (탭바 높이는 App.tsx 의 pb 가 단일 소유). minHeight:0 은 flex 자식의
    //                    기본 min-content 하한을 풀어 내부 스크롤이 동작하게 하는 필수 값.
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: PAPER1,
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          bgcolor: PAPER0,
          p: '14px 20px 12px',
          borderBottom: `1px solid ${BORDER}`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <Box component="span" aria-hidden="true" sx={{ fontSize: 20 }}>
          ✦
        </Box>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: INK_HI }}>
            AI 여행 컨셉 채팅
          </Typography>
          <Typography sx={{ fontSize: 11, color: INK_MID }}>자연어로 여행을 그려보세요</Typography>
        </Box>
      </Box>

      {/* 메시지 영역 */}
      {/* [수정] 2026-09-18 QA(M-5): ChatPage.tsx 와 동일 — 메시지를 하단 정렬로 쌓아 초기 공백 제거 */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: '16px 16px 0',
          display: 'flex',
          flexDirection: 'column',
          '& > *': { flexShrink: 0 },
          '& > *:first-of-type': { mt: 'auto' },
        }}
      >
        {flow.msgs.map((m, i) => (
          <MobileMessageBubble
            key={i}
            msg={m}
            disabled={isBusy}
            convId={flow.convId}
            onClarify={(label, value) => authGuard(() => void flow.answerClarify(label, value))}
            onConfirmYes={() => authGuard(() => void flow.confirmGenerate())}
            onConfirmNo={() => flow.declineGenerate()}
            onRetry={() => authGuard(() => void flow.confirmGenerate())}
            onStartNewConversation={handleStartNewConversation}
          />
        ))}
      </Box>

      {/* 첫 진입 추천 컨셉 칩(GET /rag/concepts 실데이터) — 대화 시작 전에만 표시 */}
      {/* [수정: 디자인 개편] 작은 칩 대신 48px 전체폭 버튼 + 실제 전송 문장(samplePrompt) 표시 */}
      {!flow.convId && showConceptChips && (
        <Box
          sx={{
            px: `${tokens.spacing[4]}px`,
            pt: `${tokens.spacing[2]}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${tokens.spacing[2]}px`,
            bgcolor: PAPER1,
          }}
        >
          {conceptsLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={48}
                  sx={{ borderRadius: `${tokens.radius.m}px` }}
                />
              ))
            : (concepts ?? []).map((concept) => (
                <ButtonBase
                  key={concept.id}
                  onClick={() => handleConceptChip(concept)}
                  aria-label={`추천 문구: ${concept.samplePrompt}`}
                  disabled={isBusy}
                  sx={{
                    width: '100%',
                    height: '48px',
                    justifyContent: 'flex-start',
                    gap: `${tokens.spacing[2]}px`,
                    bgcolor: PAPER0,
                    border: `1px solid ${BORDER}`,
                    color: INK_HI,
                    borderRadius: `${tokens.radius.m}px`,
                    px: `${tokens.spacing[4]}px`,
                    fontSize: 12.5,
                    fontWeight: 500,
                    textAlign: 'left',
                    '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: '2px' },
                  }}
                >
                  <Box component="span" aria-hidden="true" sx={{ fontSize: 16, flexShrink: 0 }}>
                    {concept.emoji}
                  </Box>
                  <Box
                    component="span"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {concept.samplePrompt}
                  </Box>
                </ButtonBase>
              ))}
        </Box>
      )}

      {/* 하단 생성 상태 시트 — phase==='generating' 일 때만 노출(D-6: 폴링 단계 라벨) */}
      {flow.phase === 'generating' && (
        <Box
          data-testid="mobile-result-sheet"
          aria-busy="true"
          aria-label="폴리오 생성 중"
          sx={{
            bgcolor: PAPER1,
            borderTop: `1px solid ${BORDER}`,
            p: `${tokens.spacing[3]}px ${tokens.spacing[4]}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${tokens.spacing[2]}px`,
          }}
        >
          <Typography role="status" aria-live="polite" sx={{ fontSize: 11, color: INK_MID }}>
            {stageLabel ?? '폴리오를 그리는 중...'}
          </Typography>
          <LinearProgress
            variant="indeterminate"
            aria-label="폴리오 생성 진행 중"
            sx={{
              height: 2,
              borderRadius: `${tokens.radius.s}px`,
              bgcolor: PAPER2,
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${BRAND}, ${GREEN})`,
              },
            }}
          />
        </Box>
      )}

      {/* 입력창 + 취소 버튼 */}
      <Box
        sx={{
          px: `${tokens.spacing[4]}px`,
          pt: `${tokens.spacing[2]}px`,
          pb: `${tokens.spacing[3]}px`,
          display: 'flex',
          gap: `${tokens.spacing[2]}px`,
          alignItems: 'flex-end',
          bgcolor: PAPER0,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        {flow.phase === 'generating' ? (
          <ButtonBase
            onClick={flow.cancelGeneration}
            aria-label="생성 취소"
            data-testid="cancel-button"
            sx={{
              flex: 1,
              bgcolor: PAPER2,
              color: INK_MID,
              border: `1px solid ${BORDER}`,
              borderRadius: `${tokens.radius.m}px`,
              p: `${tokens.spacing[3]}px`,
              fontSize: 13,
              fontWeight: 600,
              '&:focus-visible': {
                outline: `2px solid ${BRAND}`,
                outlineOffset: `${tokens.spacing[1]}px`,
              },
            }}
          >
            취소
          </ButtonBase>
        ) : (
          <>
            <TextField
              multiline
              fullWidth
              minRows={2}
              maxRows={5}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder='"친구 2명, 반려견, 조용한 카페와 산책..."'
              disabled={isBusy}
              inputProps={{ 'aria-label': '여행 자연어 입력' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: PAPER1,
                  borderRadius: `${tokens.radius.m}px`,
                  fontSize: 13,
                  color: INK_HI,
                  '& fieldset': { borderColor: BORDER },
                  '&:hover fieldset': { borderColor: BORDER },
                  '&.Mui-focused fieldset': { borderColor: BRAND },
                },
              }}
            />
            <ButtonBase
              onClick={handleSend}
              aria-label="전송"
              disabled={isBusy || !input.trim()}
              sx={{
                width: 40,
                height: 40,
                borderRadius: `${tokens.radius.m}px`,
                // [수정: 디자인 개편] 비활성일 때 글자색도 함께 바꿔 파란+흰색이 남지 않도록 한다.
                bgcolor: isBusy || !input.trim() ? PAPER2 : BRAND,
                color: isBusy || !input.trim() ? c.ink.low[L] : PAPER0,
                fontSize: 16,
                flexShrink: 0,
                '&:focus-visible': {
                  outline: `2px solid ${BRAND}`,
                  outlineOffset: `${tokens.spacing[1]}px`,
                },
              }}
            >
              ↗
            </ButtonBase>
          </>
        )}
      </Box>

      <LoginPromptModal open={isPromptOpen} intent={pendingIntent} onClose={closePrompt} />

      <style>{`@keyframes gwBlink{0%,80%,100%{opacity:.3}40%{opacity:1}}`}</style>
    </Box>
  );
}

// ── 모바일 버블 렌더러 ────────────────────────────────────────────────────────

interface MobileMessageBubbleProps {
  msg: ChatMsg;
  disabled: boolean;
  convId: string | null;
  onClarify: (label: string, value: unknown) => void;
  onConfirmYes: () => void;
  onConfirmNo: () => void;
  onRetry: () => void;
  /** G1-2: session-expired 버블 "새 대화 시작" — restoreText(실패 직전 원문, 없으면 '')를 인자로 전달 */
  onStartNewConversation: (restoreText: string) => void;
}

/**
 * [목적] ChatMsg 1건 → 모바일 버블 JSX 변환(비대칭 radius) — ChatPage.tsx MessageBubble 의 모바일 대응
 * [이유] 모바일 디자인 언어(비대칭 radius, 봇 버블 좌측 정렬)는 데스크톱과 달라 별도 렌더러 유지(기존 관행).
 * [사이드 이팩트] 없음(순수 표시 컴포넌트).
 * [수정 시 고려사항] ChatPage.tsx MessageBubble 과 분기 구조(me/clarify/confirm/pdf-ready/pdf-failed/
 *                   session-expired)는 동일하게 유지 — 한쪽만 role 을 추가하면 다른 쪽이 방치될 수 있으므로
 *                   동시 갱신 필수.
 */
/**
 * [목적] 모바일 봇 텍스트 버블 — 타이프라이터 연출(데스크톱 ChatPage.BotBubble 과 동일 정책)
 * [이유] 2026-09-20 chat-typewriter — 모바일만 답변이 통째로 나타나면 같은 서비스가 두 가지 성격을
 *        갖게 된다. 지연/커서 규칙은 useTypewriter 단일 소스라 자동으로 동기된다.
 * [사이드 이팩트] 타이핑 중 버블을 탭하면 즉시 전체 표시(skip).
 * [수정 시 고려사항] 버블 모양(비대칭 radius)은 모바일 전용 — 데스크톱과 통합하지 말 것.
 */
function MobileBotBubble({ text, avatar }: { text: string; avatar: JSX.Element }): JSX.Element {
  const { displayed, isTyping, skip } = useTypewriter(text);

  return (
    <Box sx={{ display: 'flex', gap: `${tokens.spacing[2]}px`, mb: `${tokens.spacing[3]}px` }}>
      {avatar}
      <Box sx={{ maxWidth: '80%' }}>
        <Box
          onClick={isTyping ? skip : undefined}
          sx={{
            bgcolor: PAPER0,
            color: INK_HI,
            borderRadius: '4px 16px 16px 16px',
            px: `${tokens.spacing[3]}px`,
            py: `${tokens.spacing[2]}px`,
            fontSize: 13,
            lineHeight: 1.55,
            whiteSpace: 'pre-line',
            boxShadow: SHADOW_CARD,
            cursor: isTyping ? 'pointer' : 'default',
          }}
        >
          {displayed}
          {isTyping && (
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                display: 'inline-block',
                width: '2px',
                height: '1em',
                verticalAlign: '-2px',
                ml: '1px',
                borderRadius: '1px',
                bgcolor: BRAND,
                animation: 'gcTypeCaretM .9s steps(1,end) infinite',
                '@keyframes gcTypeCaretM': {
                  '0%, 45%': { opacity: 1 },
                  '50%, 95%': { opacity: 0 },
                  '100%': { opacity: 1 },
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

function MobileMessageBubble({
  msg,
  disabled,
  convId,
  onClarify,
  onConfirmYes,
  onConfirmNo,
  onRetry,
  onStartNewConversation,
}: MobileMessageBubbleProps): JSX.Element {
  const avatar = (
    <Box
      aria-hidden="true"
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        flexShrink: 0,
        background: `linear-gradient(135deg, ${BRAND}, ${GREEN})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 800,
        color: PAPER0,
        boxShadow: SHADOW_CARD,
      }}
    >
      ✦
    </Box>
  );

  if (msg.role === 'me') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row-reverse', mb: `${tokens.spacing[3]}px` }}>
        <Box sx={{ maxWidth: '80%' }}>
          <Box
            sx={{
              bgcolor: BRAND,
              color: PAPER0,
              borderRadius: '16px 16px 4px 16px',
              px: `${tokens.spacing[3]}px`,
              py: `${tokens.spacing[2]}px`,
              fontSize: 13,
              lineHeight: 1.55,
              whiteSpace: 'pre-line',
            }}
          >
            {msg.text}
          </Box>
        </Box>
      </Box>
    );
  }

  if (msg.role === 'bot') {
    return <MobileBotBubble text={msg.text} avatar={avatar} />;
  }

  if (msg.role === 'clarify') {
    return (
      <Box sx={{ display: 'flex', gap: `${tokens.spacing[2]}px`, mb: `${tokens.spacing[3]}px` }}>
        {avatar}
        <Box
          role="group"
          aria-label="선택지 확인"
          sx={{
            maxWidth: '80%',
            bgcolor: PAPER0,
            borderRadius: '4px 16px 16px 16px',
            px: `${tokens.spacing[3]}px`,
            py: `${tokens.spacing[2]}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${tokens.spacing[2]}px`,
            boxShadow: SHADOW_CARD,
          }}
        >
          {msg.text && (
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: INK_HI }}>
              {msg.text}
            </Typography>
          )}
          {(msg.clarifyOptions ?? []).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${tokens.spacing[1]}px` }}>
              {(msg.clarifyOptions ?? []).map((opt, idx) => (
                <ButtonBase
                  key={idx}
                  onClick={() => onClarify(opt.label, opt.value)}
                  disabled={disabled}
                  aria-label={opt.label}
                  sx={{
                    borderRadius: `${tokens.radius.xl}px`,
                    px: `${tokens.spacing[3]}px`,
                    py: `${tokens.spacing[1]}px`,
                    fontSize: 12,
                    fontWeight: 500,
                    bgcolor: PAPER1,
                    color: INK_MID,
                    border: `1px solid ${BORDER}`,
                    opacity: disabled ? 0.48 : 1,
                    '&:focus-visible': {
                      outline: `2px solid ${BRAND}`,
                      outlineOffset: `${tokens.spacing[1]}px`,
                    },
                  }}
                >
                  {opt.label}
                </ButtonBase>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  if (msg.role === 'confirm') {
    return (
      <Box sx={{ display: 'flex', gap: `${tokens.spacing[2]}px`, mb: `${tokens.spacing[3]}px` }}>
        {avatar}
        <Box
          role="group"
          aria-label="폴리오 생성 확인"
          sx={{
            maxWidth: '80%',
            bgcolor: PAPER0,
            borderRadius: '4px 16px 16px 16px',
            px: `${tokens.spacing[3]}px`,
            py: `${tokens.spacing[2]}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${tokens.spacing[2]}px`,
            boxShadow: SHADOW_CARD,
          }}
        >
          <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: INK_HI, fontWeight: 500 }}>
            {msg.text}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${tokens.spacing[1]}px` }}>
            <ButtonBase
              onClick={onConfirmYes}
              disabled={disabled}
              aria-label="네, 만들어주세요"
              data-testid="confirm-yes-button"
              sx={{
                borderRadius: `${tokens.radius.xl}px`,
                px: `${tokens.spacing[3]}px`,
                py: `${tokens.spacing[1]}px`,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: disabled ? PAPER2 : BRAND,
                color: disabled ? INK_MID : PAPER0,
                border: `1px solid ${disabled ? BORDER : BRAND}`,
                opacity: disabled ? 0.48 : 1,
                '&:focus-visible': {
                  outline: `2px solid ${BRAND}`,
                  outlineOffset: `${tokens.spacing[1]}px`,
                },
              }}
            >
              네, 만들어주세요
            </ButtonBase>
            <ButtonBase
              onClick={onConfirmNo}
              disabled={disabled}
              aria-label="조금 더 다듬을래요"
              data-testid="confirm-refine-button"
              sx={{
                borderRadius: `${tokens.radius.xl}px`,
                px: `${tokens.spacing[3]}px`,
                py: `${tokens.spacing[1]}px`,
                fontSize: 12,
                fontWeight: 500,
                bgcolor: 'transparent',
                color: INK_MID,
                border: `1px solid ${BORDER}`,
                opacity: disabled ? 0.48 : 1,
                '&:focus-visible': {
                  outline: `2px solid ${BRAND}`,
                  outlineOffset: `${tokens.spacing[1]}px`,
                },
              }}
            >
              조금 더 다듬을래요
            </ButtonBase>
          </Box>
        </Box>
      </Box>
    );
  }

  if (msg.role === 'pdf-ready') {
    return (
      <Box sx={{ display: 'flex', gap: `${tokens.spacing[2]}px`, mb: `${tokens.spacing[3]}px` }}>
        {avatar}
        <Box
          sx={{
            maxWidth: '80%',
            bgcolor: PAPER0,
            borderRadius: '4px 16px 16px 16px',
            px: `${tokens.spacing[3]}px`,
            py: `${tokens.spacing[2]}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${tokens.spacing[2]}px`,
            boxShadow: SHADOW_CARD,
          }}
        >
          <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: INK_HI }}>
            폴리오가 준비됐어요! 🎉
          </Typography>
          {/* [목적] "폴리오 보기" 주 버튼(portfolioId 있을 때) + PDF 다운로드 부가 버튼(항상 유지, G1-1)
              [이유] ChatPage(데스크톱)와 동일 CTA 승격 — portfolioId null 이면 기존 PDF 버튼만 노출(하위호환). */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${tokens.spacing[2]}px` }}>
            {msg.portfolioId && (
              <ButtonBase
                component={RouterLink}
                to={`/folio/${msg.portfolioId}`}
                aria-label="완성된 폴리오 보기"
                sx={{
                  borderRadius: `${tokens.radius.xl}px`,
                  px: `${tokens.spacing[3]}px`,
                  py: `${tokens.spacing[1]}px`,
                  fontSize: 12,
                  fontWeight: 700,
                  bgcolor: BRAND,
                  color: PAPER0,
                  '&:focus-visible': {
                    outline: `2px solid ${BRAND}`,
                    outlineOffset: `${tokens.spacing[1]}px`,
                  },
                }}
              >
                폴리오 보기
              </ButtonBase>
            )}
            {convId && (
              <ButtonBase
                component="a"
                href={buildPdfUrl(convId)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="폴리오 PDF 다운로드"
                sx={{
                  borderRadius: `${tokens.radius.xl}px`,
                  px: `${tokens.spacing[3]}px`,
                  py: `${tokens.spacing[1]}px`,
                  fontSize: 12,
                  fontWeight: msg.portfolioId ? 500 : 700,
                  bgcolor: msg.portfolioId ? 'transparent' : BRAND,
                  color: msg.portfolioId ? INK_MID : PAPER0,
                  border: msg.portfolioId ? `1px solid ${BORDER}` : 'none',
                  '&:focus-visible': {
                    outline: `2px solid ${BRAND}`,
                    outlineOffset: `${tokens.spacing[1]}px`,
                  },
                }}
              >
                PDF 다운로드
              </ButtonBase>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  if (msg.role === 'pdf-failed') {
    return (
      <Box sx={{ display: 'flex', gap: `${tokens.spacing[2]}px`, mb: `${tokens.spacing[3]}px` }}>
        {avatar}
        <Box
          role="alert"
          aria-live="assertive"
          sx={{
            maxWidth: '80%',
            bgcolor: PAPER0,
            borderRadius: '4px 16px 16px 16px',
            px: `${tokens.spacing[3]}px`,
            py: `${tokens.spacing[2]}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${tokens.spacing[2]}px`,
            boxShadow: SHADOW_CARD,
          }}
        >
          <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: INK_HI }}>
            {/* [수정: QA-P1-02] msg.text 는 useChatFlow 가 generationErrorMessage() 로 이미 한글 매핑한
                문구다 — BE rag.* 코드를 화면에 그대로 노출하지 않는다. */}
            {msg.text || '폴리오 생성에 실패했어요.'}
          </Typography>
          <ButtonBase
            onClick={onRetry}
            disabled={disabled}
            aria-label="폴리오 생성 다시 시도"
            data-testid="retry-button"
            sx={{
              alignSelf: 'flex-start',
              borderRadius: `${tokens.radius.xl}px`,
              px: `${tokens.spacing[3]}px`,
              py: `${tokens.spacing[1]}px`,
              fontSize: 12,
              fontWeight: 700,
              // [수정: 디자인 개편 — 비활성 버튼은 회색으로] opacity만 낮춰는 대신 배경/글자색을 명확히 바꿔 구분한다.
              bgcolor: disabled ? PAPER2 : BRAND,
              color: disabled ? c.ink.low[L] : PAPER0,
              '&:focus-visible': {
                outline: `2px solid ${BRAND}`,
                outlineOffset: `${tokens.spacing[1]}px`,
              },
            }}
          >
            다시 시도
          </ButtonBase>
        </Box>
      </Box>
    );
  }

  // session-expired (G1-2) — convId 만료/미발견 복구 UI: 안내 문구 + "새 대화 시작"(마지막 입력 복원)
  return (
    <Box sx={{ display: 'flex', gap: `${tokens.spacing[2]}px`, mb: `${tokens.spacing[3]}px` }}>
      {avatar}
      <Box
        role="alert"
        sx={{
          maxWidth: '80%',
          bgcolor: PAPER0,
          borderRadius: '4px 16px 16px 16px',
          px: `${tokens.spacing[3]}px`,
          py: `${tokens.spacing[2]}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: `${tokens.spacing[2]}px`,
          boxShadow: SHADOW_CARD,
        }}
      >
        <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: INK_HI }}>
          대화 세션이 만료됐어요. 새 대화를 시작해 주세요.
        </Typography>
        <ButtonBase
          onClick={() => onStartNewConversation(msg.text)}
          aria-label="새 대화 시작"
          data-testid="start-new-conversation-button"
          sx={{
            alignSelf: 'flex-start',
            borderRadius: `${tokens.radius.xl}px`,
            px: `${tokens.spacing[3]}px`,
            py: `${tokens.spacing[1]}px`,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: BRAND,
            color: PAPER0,
            '&:focus-visible': {
              outline: `2px solid ${BRAND}`,
              outlineOffset: `${tokens.spacing[1]}px`,
            },
          }}
        >
          새 대화 시작
        </ButtonBase>
      </Box>
    </Box>
  );
}
